import { useField, useFormikContext } from "formik";
import { FC, Fragment, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import "react-datepicker/dist/react-datepicker.css";
import {
  customStyles,
  EMPTY_STRING,
} from "../../common/constants/common.constants";
import { IOption } from "../../common/interfaces/common.interface";
import infoCircle from "../../assets/tooltipIcon.svg";
import "./WizardField.scss";
import Tooltip from "../Tooltip/Tooltip";
import { FilterOptionOption } from "react-select/dist/declarations/src/filters";
import { blockInvalidChar } from "../../utilities/helpers";
import MintSelect from "../CreateProposalWizard/CreateProposal/TransferProposal/MintSelect/MintSelect";
import { ITreasuryTokenAccInfo } from "../../common/interfaces/clubs.interface";

export const customStylesSelect = {
  option: () => ({
    cursor: "pointer",
    padding: "1em 2em",
    fontSize: "0.9em",
    fontFamily: "Montserrat,sans-serif",
    fontWeight: 100,
    //backgroundColor: "rgba(255, 255, 255, 0.03)",
    margin: 0,
    zIndex: 50,
    width: "100%",
    "&:hover": {
      opacity: 0.7,
      color: "white",
      cursor: "pointer",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "white",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "transparent",
    borderRadius: "8px",
  }),
  menuList: (provided) => ({
    ...provided,
    backgroundColor: "#0C121D",
    borderRadius: "8px",
  }),
  input: (provided) => ({
    ...provided,
    color: "white",
    fontSize: "0.9em",
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    whiteSpace: "nowrap",
    overflow: "hidden",
    flexWrap: "nowrap",
    color: "white",
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "0.9em",
    fontWeight: "100",
    fontFamily: "Montserrat, sans-serif",
    marginLeft: "1em",
  }),
  control: (provided) => ({
    ...provided,
    ...controlObject,
  }),
};
const controlObject = {
  borderRadius: "10px",
  fontSize: "0.9em",
  cursor: "pointer",
  background: "rgba(255, 255, 255, 0.03)",
  border: "none",
  color: "white",
  fontFamily: "Montserrat",
  boxShadow: "none",
  height: "46.78px",
  padding: "0 1em",
  width: "100%",
};
interface WizardFieldProps {
  name: string;
  placeholder?: string;
  persistentPlaceholder?: string;
  type: string;
  selectOptions?: IOption[];
  label?: string;
  tooltipTitle?: string;
  tooltipDescription?: string;
  leftPosition?: number;
  disabled?: boolean;
  initialDate?: Date;
  values?: IOption[];
  required?: boolean;
  maxDecimalsLength?: number;
  isBuyTooltip?: boolean;
  noOptionsMessage?: string;
  isValidField?: (e) => boolean;
  validateField?: () => void;
  onChange?: (e: any) => void;
  inputError?: boolean;
  mintSelectItems?: ITreasuryTokenAccInfo[];
  isFundraisingConfig?: boolean;
  chosenMint?: string;
  isSearchable?: boolean;
  defaultSelectValue?: string;
  defaultCreatableSelect?: IOption[];
}

const WizardField: FC<WizardFieldProps> = (props) => {
  const { setFieldValue, errors, handleBlur, setFieldTouched } =
    useFormikContext();
  const [field, meta] = useField(props);
  const [selectedDate, setSelectedDate] = useState(props.initialDate);
  const [isTootlipVisible, toggleTooltip] = useState(false);
  //TODO@milica: this is hacked solution, check Formik documentation
  const [dateFieldTouched, setDateFieldTouched] = useState(false);

  useEffect(() => {
    setFieldTouched(field.name, false);
  }, []);

  const customFieldValidation = (e) => {
    handleBlur(e);
    if (props.validateField) {
      props.validateField();
    }
  };

  const changeSelectedOption = (e: any) => {
    setFieldValue(props.name, e?.value);
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const validateNewTag = (
    option: FilterOptionOption<IOption>,
    value: string
  ): boolean => {
    if (!props.values) {
      return true;
    }
    if (value.trim() === EMPTY_STRING) {
      return false;
    }
    const foundTag = props.values.find(
      (tag) => tag.value.toLowerCase().trim() === value.toLowerCase().trim()
    );
    if (foundTag) {
      return false;
    }
    return true;
  };

  if (props.type === "textarea") {
    return (
      <div className="input">
        {props.label && (
          <p className="input__label">
            {props.label} {props.required && <span>*</span>}
          </p>
        )}
        <textarea
          disabled={props.disabled}
          className={`${
            errors[field.name] && meta.touched
              ? "wizard-field wizard-field--error"
              : "wizard-field"
          }`}
          {...field}
          placeholder={props.placeholder}
          maxLength={500}
          onBlur={(e) => handleBlur(e)}
        />
      </div>
    );
  }

  if (props.type === "date") {
    return (
      <div className="input">
        {props.label && (
          <p className="input__label">
            {props.label} {props.required && <span>*</span>}
          </p>
        )}

        <DatePicker
          selected={selectedDate}
          onChange={(e) => {
            setFieldValue(props.name, e);
            setSelectedDate(e!);
            setDateFieldTouched(true);
          }}
          dateFormat="dd.MM.yyyy"
          popperPlacement="bottom"
        />
      </div>
    );
  }

  if (props.type === "creatable-select") {
    return (
      <div className="input">
        {props.label && <p className="input__label">{props.label}</p>}
        <CreatableSelect
          isMulti
          isDisabled={props.disabled}
          onChange={props.onChange}
          styles={
            errors[field.name] && meta.touched
              ? {
                  ...customStyles,
                  control: (provided) => ({
                    ...provided,
                    ...controlObject,
                    border: "1px solid red",
                    borderRadius: "8px",
                    minHeight: "5em",
                    "&:hover": {
                      border: "1px solid red",
                    },
                  }),
                }
              : customStyles
          }
          placeholder={props.placeholder}
          isValidNewOption={props.isValidField}
          defaultValue={props.defaultCreatableSelect}
          value={props.values}
          noOptionsMessage={(input) => `${props.noOptionsMessage}`}
          formatCreateLabel={(input) => `${props.noOptionsMessage}`}
          filterOption={(option, inputValue) =>
            validateNewTag(option, inputValue)
          }
          options={props.selectOptions}
        />
      </div>
    );
  }

  if (props.type === "mint-select" && props.mintSelectItems) {
    return (
      <div className="input input__mint-select">
        {props.label && <p className="input__label">{props.label}</p>}
        <MintSelect
          treasuryTokenAccounts={props.mintSelectItems}
          isForm={true}
          setFieldValue={setFieldValue}
          isFundraisingConfig={props.isFundraisingConfig}
          chosenMint={props.chosenMint}
        />
      </div>
    );
  }

  if (props.type === "select") {
    return (
      <Fragment>
        {props.label && (
          <p className="input__label">
            {props.label} {props.required && <span>*</span>}
          </p>
        )}
        <Select
          options={props.selectOptions}
          className="field__select"
          classNamePrefix="select"
          isDisabled={props.disabled}
          styles={
            errors[field.name] && meta.touched
              ? {
                  ...customStylesSelect,
                  control: (provided) => ({
                    ...provided,
                    ...controlObject,
                    border: "1px solid red",
                    borderRadius: "8px",
                    "&:hover": {
                      border: "1px solid red",
                    },
                  }),
                }
              : customStylesSelect
          }
          onChange={(e: any) => {
            changeSelectedOption(e);
          }}
          placeholder={props.placeholder}
          isSearchable={props.isSearchable}
          onBlur={(e) => handleBlur(e)}
          value={
            props.selectOptions
              ? props.selectOptions.find(
                  (option) => option.value === field.value
                )
              : ""
          }
        />
      </Fragment>
    );
  }

  return (
    <div className="input" data-placeholder={props.persistentPlaceholder}>
      {props.label && (
        <div className="input__label-wrapper">
          <p
            className={`${
              props.disabled ? "input__label disabled" : "input__label "
            }`}
          >
            {props.label} {props.required && <span>*</span>}
          </p>
          {(props.tooltipTitle || props.isBuyTooltip) && (
            <div
              className="input__tooltip"
              onMouseLeave={() => toggleTooltip(false)}
            >
              {!props.isBuyTooltip && (
                <Tooltip
                  tooltipTitle={props.tooltipTitle}
                  tooltipDescription={props.tooltipDescription!}
                  leftPositioning={props.leftPosition ?? 0}
                  icon={infoCircle}
                />
              )}
            </div>
          )}
        </div>
      )}
      <input
        className={`${
          errors[field.name] && meta.touched
            ? "wizard-field wizard-field--error"
            : "wizard-field"
        }`}
        {...field}
        type={props.type}
        placeholder={props.placeholder}
        min="0"
        onKeyDown={
          props.type === "number" ? (e) => blockInvalidChar(e) : undefined
        }
        step={props.maxDecimalsLength}
        onBlur={customFieldValidation}
        onInput={props.onChange}
        disabled={props.disabled}
        onWheel={(event: any) => event.target.blur()}
      />
      {props.inputError && errors[field.name] && meta.touched && (
        <p className="input__error">{errors[props.name]}</p>
      )}
    </div>
  );
};

export default WizardField;
